import React, { useState, useEffect } from "react";
import SearchBarLarge from "../components/SearchBarLarge";
import { useSearch } from "../util/SearchContext"; // Import the search context


export default function HomePage() {
  const { handleSearch, setSearchQuery } = useSearch(); // Get global search functions
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);


  const placeholders = [
    "Search: vegetarian soup with lentils",
    "Search: low-carb stir fry",
    "Search: chicken tacos",
    "Search: Mexican dinner recipes",
    "Submit a recipe: https://cookieandkate.com/pinto-posole-recipe/",
    "Submit a recipe: https://www.feastingathome.com/kimchi-fried-rice/",
    "Submit a recipe: https://www.halfbakedharvest.com/chipotle-sweet-potato-tacos/",
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex((prevIndex) => (prevIndex + 1) % placeholders.length);
    }, 3000); // Rotate every 3 seconds
    return () => clearInterval(interval);
  }, []);

  const handleAutoSearch = (inputValue) => {
    setSearchQuery(inputValue);
    setIsSearching(false);

    setTimeout(() => setIsSearching(true), 1500);
    setTimeout(() => setIsSearching(false), 1700);
    setTimeout(() => {
      handleSearch(inputValue);
      setIsSearching(false);
    }, 2000);
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <header className="bg-white py-12 text-center shadow-sm px-2 sm:px-4">
        <h1 className="text-4xl font-semibold text-green-700 mb-4">Welcome to Potluck</h1>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">
          Potluck simplifies recipes from around the web, making them easier to read and follow.
          Use AI-powered search to find the best recipes for your needs.
        </p>

        {/* Search Bar - Now More Prominent */}
        <div className="flex justify-center mt-6">
          <SearchBarLarge placeholder={placeholders[placeholderIndex]} isSearching={isSearching} />
        </div>

        {/* Condensed "How to Use" Instruction */}
        <p className="text-gray-500 text-sm mt-2">
          Paste a recipe URL or search with keywords like recipe description, cuisine, or ingredients.
        </p>

        {/* Demo Buttons - Styled as Light Suggestions */}
        <div className="mt-6 text-center">
          <p className="text-gray-600 text-sm mb-1">Or try a quick example:</p>
          <div className="flex justify-center space-x-3">
            <button
              className="px-4 py-2 text-gray-600 text-sm border border-gray-300 rounded-full hover:bg-gray-100 transition"
              onClick={() => handleAutoSearch("https://www.halfbakedharvest.com/chipotle-sweet-potato-tacos/")}
            >
              🍽️ Translate a recipe
            </button>
            <button
              className="px-4 py-2 text-gray-600 text-sm border border-gray-300 rounded-full hover:bg-gray-100 transition"
              onClick={() => handleAutoSearch("vegetarian soup with lentils")}
            >
              🔍 Search with keywords
            </button>
          </div>
        </div>
      </header>

      {/* How It Works Section - More Visual, Less Text */}
      <section className="max-w-4xl mx-auto mt-12 px-6">
        <h2 className="text-2xl font-semibold text-gray-800 text-center mb-8">How It Works</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 shadow-md rounded-lg text-center">
            <span className="text-4xl text-green-600 mb-4 block">1️⃣</span>
            <h3 className="text-lg font-semibold mb-2">Paste a link or search</h3>
            <p className="text-gray-600">Paste a URL to extract a recipe, or use keywords to search.</p>
          </div>
          <div className="bg-white p-6 shadow-md rounded-lg text-center">
            <span className="text-4xl text-green-600 mb-4 block">2️⃣</span>
            <h3 className="text-lg font-semibold mb-2">AI refines it</h3>
            <p className="text-gray-600">Our AI formats recipes for clarity, making them easy to read and follow.</p>
          </div>
          <div className="bg-white p-6 shadow-md rounded-lg text-center">
            <span className="text-4xl text-green-600 mb-4 block">3️⃣</span>
            <h3 className="text-lg font-semibold mb-2">Cook & save</h3>
            <p className="text-gray-600">Follow your simplified recipe with ease and save it for later.</p>
          </div>
        </div>
      </section>


      {/* Feature Highlights Section */}
      <section className="max-w-5xl mx-auto mt-16 px-6">
        <h2 className="text-2xl font-semibold text-gray-800 text-center mb-8">Why Use Potluck?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

          <div className="bg-white p-6 shadow-md rounded-lg flex items-center space-x-4">
            <span className="text-green-700 text-3xl">📋</span>
            <p className="text-gray-600">
              <span className="font-semibold">Recipes, simplified:&nbsp;</span>
              Potluck refines formatting and improves readability while staying true to the inspiration behind each dish.
            </p>
          </div>

          <div className="bg-white p-6 shadow-md rounded-lg flex items-center space-x-4">
            <span className="text-green-700 text-3xl">🤖</span>
            <p className="text-gray-600">
              <span className="font-semibold">AI-powered search:&nbsp;</span>
              Find the best recipes instantly—without the frustration of endless browsing.
            </p>
          </div>

          <div className="bg-white p-6 shadow-md rounded-lg flex items-center space-x-4">
            <span className="text-green-700 text-3xl">💾</span>
            <p className="text-gray-600">
              <span className="font-semibold">Your recipe collection:&nbsp;</span>
              Save and organize your favorite recipes in one place for quick access anytime.
            </p>
          </div>

          <div className="bg-white p-6 shadow-md rounded-lg flex items-center space-x-4">
            <span className="text-green-700 text-3xl">🌍</span>
            <p className="text-gray-600">
              <span className="font-semibold">Supporting food creators:&nbsp;</span>
              We help home cooks enjoy recipes while recognizing the talented bloggers behind them.
            </p>
          </div>

        </div>
      </section>

      &nbsp;
    </div>
  );
}
